import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import { EllipsisText, formatDateTime, JsonBlock } from '../Assessments/shared';
import {
  ValidityCheck,
  ValidityResponse,
  ValiditySummary,
  ValidityVerdictDocument,
  ValidityWorstCheck,
} from './actionTypes';
import {
  activateScenario,
  activationConflict,
  fetchScenarioValidity,
  recomputeScenarioValidity,
} from './actionsApi';
import { readableError } from './errors';

/**
 * The quality audit as EVIDENCE, and the activation that reads it.
 *
 * Three rules the backend encodes and this screen has to make visible, or the
 * control is theatre:
 *   - a stale verdict is treated as absent, because signing off using a
 *     grading of different content looks like diligence and is not;
 *   - a WARN never blocks, so nobody learns to type a sentence and continue;
 *   - the override reason is stored on the verdict that was on screen, so the
 *     audit says WHICH grading a human overrode.
 */

/** The minimum the backend accepts (ACTIVATION_OVERRIDE_MIN_CHARS). */
export const OVERRIDE_MIN_CHARS = 20;

/**
 * The sentence of a refusal that is neither the gate nor the preflight: a
 * candidate that cannot be promoted, a retired or read-only key. The body is
 * `{code: {message}}`, and the message is what the administrator needs.
 */
export const otherRefusal = (conflict: Record<string, unknown>): string =>
  Object.values(conflict)
    .map(value => {
      if (value && typeof value === 'object' && 'message' in value) {
        return String((value as { message?: unknown }).message ?? '');
      }
      return typeof value === 'string' ? value : JSON.stringify(value);
    })
    .filter(Boolean)
    .join(' ');

const VERDICT_COLOR: Record<string, string> = {
  pass: 'green',
  warn: 'orange',
  insufficient: 'purple',
  fail: 'red',
};

/**
 * What a verdict tag means, said in the terms of what it costs.
 *
 * The tags carried no tooltip and no title attribute at all, so thirteen rows
 * reading a red FAIL beside a green ACTIVE were either "the page is broken" or
 * "ACTIVE means fine", depending on the reader. It is neither.
 */
export const verdictTooltip = (
  verdict: string,
  { active, overridden }: { active?: boolean; overridden?: boolean } = {},
): string => {
  if (verdict === 'fail') {
    if (active && !overridden) {
      return (
        'Live and grading trainees although the audit says this answer key ' +
        'cannot separate skill from clicking. No override is on record.'
      );
    }
    if (active) {
      return (
        'Live and grading trainees. The audit failed it and an admin activated ' +
        'it anyway with a written reason, which is stored on the verdict.'
      );
    }
    return (
      'The audit says this answer key cannot separate skill from clicking. ' +
      'Nobody is being graded by it yet; activating it needs a written reason.'
    );
  }
  if (verdict === 'insufficient') {
    return (
      'The audit could not measure this one either way — there was not enough ' +
      'scored content to read. That is not evidence it is good or bad.'
    );
  }
  if (verdict === 'warn') {
    return 'Worth reading before you activate, but it blocks nothing.';
  }
  if (verdict === 'pass') {
    return 'Every check the audit runs passed on the content it graded.';
  }
  return 'No verdict is on record for this answer key.';
};

/** The rollup cell. A verdict about different content is labelled stale
 *  rather than shown as if it graded what is there now.
 *
 *  No verdict reads NOT CHECKED, never "not graded": "not graded" is what an
 *  instrument row says when the answer key does not mark it, and this tag is
 *  about the key itself — the quality audit has not been run on it. */
export const ValidityTag: React.FC<{
  validity?: ValiditySummary | null;
  /** Whether this answer key is grading people right now. */
  active?: boolean;
}> = ({ validity, active }) => {
  if (!validity || !validity.verdict) {
    return (
      <Tooltip title={verdictTooltip('')}>
        <Tag>NOT CHECKED</Tag>
      </Tooltip>
    );
  }
  const verdict = String(validity.verdict).toLowerCase();
  return (
    <Space size={[4, 4]} wrap className="assessment-tag-group">
      <Tooltip
        title={verdictTooltip(verdict, {
          active,
          overridden: validity.overridden,
        })}
      >
        <Tag color={VERDICT_COLOR[verdict] || 'default'}>
          {verdict.toUpperCase()}
        </Tag>
      </Tooltip>
      {(validity.current === false ||
        validity.gate_version_current === false) && (
        <Tooltip title="This verdict graded different content, or an earlier version of the audit. It is not evidence about what is there now.">
          <Tag color="default">STALE</Tag>
        </Tooltip>
      )}
      {validity.overridden && (
        <Tooltip title="An admin activated this over a failing audit, with a written reason stored on the verdict.">
          <Tag color="gold">OVERRIDDEN</Tag>
        </Tooltip>
      )}
    </Space>
  );
};

/** A check key as a sentence, with the dotted key kept as the subtitle: the
 *  key is what an engineer greps for, the sentence is what an admin reads. */
export const CHECK_LABELS: Record<string, string> = {
  'no_skill.floor': 'No-skill floor',
  'no_skill.random': 'Random-candidate spread',
  'predictive.counter_signal': 'A signal pointing the wrong way',
  'predictive.hit_rate': 'Directional hit rate',
  'coverage.macro_clean': 'Stray dates in the macro text',
  'coverage.periods': 'Periods with scored content',
  'coverage.instruments': 'Instruments the key grades',
  'release.identity': 'Answer key identity',
  'coverage.vote': 'Instruments with a scored view',
};

/**
 * A key with no sentence of its own is humanised rather than truncated: the
 * last segment alone turned `coverage.vote` into "vote", which names nothing.
 * The whole key, spelled out, at least says which family it belongs to — and
 * the dotted key is still printed underneath for anyone who greps.
 */
export const checkLabel = (key: string) => {
  if (CHECK_LABELS[key]) return CHECK_LABELS[key];
  const words = String(key || '')
    .replace(/[._]/g, ' ')
    .trim();
  if (!words) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** One line of evidence on a rollup row: which check failed, what it measured
 *  against what it needed, and how many people the answer key has graded. */
export const WorstCheckLine: React.FC<{
  check?: ValidityWorstCheck | null;
  sessionCount?: number;
  gateVersion?: string;
}> = ({ check, sessionCount, gateVersion }) => {
  if (!check) return null;
  const measured = check.value == null ? '' : String(check.value);
  const needed = check.threshold == null ? '' : String(check.threshold);
  const sessions =
    sessionCount == null
      ? ''
      : ` · ${sessionCount} session${sessionCount === 1 ? '' : 's'} graded`;
  return (
    <Space direction="vertical" size={0} data-testid="worst-check">
      <Tooltip title={check.detail || undefined} placement="topLeft">
        <Typography.Text type="secondary" className="assessment-ellipsis">
          {checkLabel(check.key)}
          {measured ? ` ${measured}` : ''}
          {needed ? ` / bar ${needed}` : ''}
          {sessions}
        </Typography.Text>
      </Tooltip>
      <EllipsisText
        code
        value={gateVersion ? `${check.key} · ${gateVersion}` : check.key}
      />
    </Space>
  );
};

const checkColumns: any[] = [
  {
    title: 'Check',
    dataIndex: 'key',
    width: 220,
    render: (value: string) => <EllipsisText code value={value} />,
  },
  {
    title: 'Verdict',
    dataIndex: 'verdict',
    width: 130,
    render: (value: string, row: ValidityCheck) => (
      <Space size={[4, 4]} wrap className="assessment-tag-group">
        <Tag color={VERDICT_COLOR[String(value).toLowerCase()] || 'default'}>
          {String(value).toUpperCase()}
        </Tag>
        {row.vacuous && <Tag color="default">EXAMINED NOTHING</Tag>}
      </Space>
    ),
  },
  {
    title: 'Found',
    dataIndex: 'value',
    width: 120,
    render: (value: unknown) =>
      value == null ? '—' : <EllipsisText value={JSON.stringify(value)} />,
  },
  {
    title: 'Threshold',
    dataIndex: 'threshold',
    width: 120,
    render: (value: unknown) =>
      value == null ? '—' : <EllipsisText value={JSON.stringify(value)} />,
  },
  {
    title: 'Examined',
    dataIndex: 'examined',
    width: 100,
    render: (value: number | null) => (value == null ? '—' : value),
  },
  {
    title: 'What it means',
    dataIndex: 'detail',
    render: (value: string) =>
      value || <Typography.Text type="secondary">—</Typography.Text>,
  },
];

export const ValidityEvidence: React.FC<{
  response: ValidityResponse | null;
}> = ({ response }) => {
  const verdict = response?.verdict as ValidityVerdictDocument | null;
  if (!verdict) {
    return (
      <Alert
        className="assessment-callout"
        type="warning"
        showIcon
        title="This answer key has never been through the quality audit"
        description="Nothing has measured whether it separates a candidate who thinks from one who clicks. Recompute the audit to find out."
      />
    );
  }
  const stale = response?.current === false;
  const verdictValue = String(verdict.verdict || '').toLowerCase();
  const release = (verdict.sections?.release || {}) as {
    ok?: boolean;
    blockers?: unknown[];
  };
  return (
    <div className="assessment-detail-stack">
      {stale && (
        <Alert
          className="assessment-callout"
          type="warning"
          showIcon
          title="This verdict describes a different answer key"
          description="The answer key changed after it was graded, so this grading is not evidence about what is stored now. Recompute the audit before acting on it."
        />
      )}
      <Card size="small" title="Quality audit verdict">
        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label="Verdict">
            <Tag color={VERDICT_COLOR[verdictValue] || 'default'}>
              {verdictValue.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Audit version">
            <EllipsisText code value={verdict.gate_version} />
          </Descriptions.Item>
          <Descriptions.Item label="Computed">
            {formatDateTime(verdict.computed_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Computed by">
            {verdict.computed_by || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Triggered by">
            {verdict.source || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Content fingerprint">
            <EllipsisText code value={verdict.content_fingerprint} />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {verdict.override_reason && (
        <Alert
          className="assessment-callout"
          type="warning"
          showIcon
          title={`This verdict was overridden by ${
            verdict.overridden_by || 'an admin'
          } on ${formatDateTime(verdict.overridden_at)}`}
          description={verdict.override_reason}
        />
      )}

      <Card size="small" title={`Checks (${(verdict.checks || []).length})`}>
        <Table
          rowKey="key"
          className="assessment-table"
          size="small"
          tableLayout="fixed"
          dataSource={verdict.checks || []}
          columns={checkColumns}
          scroll={{ x: 1100 }}
          pagination={false}
        />
      </Card>

      {Array.isArray(release.blockers) && release.blockers.length > 0 && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={`Strict release blockers (${release.blockers.length})`}
          description={
            <ul>
              {release.blockers.map((blocker, index) => (
                <li key={index}>
                  {typeof blocker === 'string'
                    ? blocker
                    : JSON.stringify(blocker)}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {verdict.sections && (
        <Card size="small" title="Measurements">
          <JsonBlock compact value={verdict.sections} />
        </Card>
      )}
    </div>
  );
};

type ActivateProps = {
  open: boolean;
  onClose: () => void;
  scenarioCode: string;
  scenarioName?: string;
  activeAlready?: boolean;
  onActivated?: () => void;
};

/** The one place an answer key starts grading people. */
export const ActivatePanel: React.FC<ActivateProps> = ({
  open,
  onClose,
  scenarioCode,
  scenarioName,
  activeAlready,
  onActivated,
}) => {
  const [response, setResponse] = useState<ValidityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState<Record<string, unknown> | null>(
    null,
  );
  const [reason, setReason] = useState('');
  const [activated, setActivated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setResponse(await fetchScenarioValidity(scenarioCode));
    } catch (requestError) {
      setError((requestError as Error).message);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [scenarioCode]);

  useEffect(() => {
    if (open) {
      setConflict(null);
      setActivated(false);
      setReason('');
      void load();
    }
  }, [open, load]);

  const recompute = async () => {
    setBusy(true);
    setError('');
    setConflict(null);
    try {
      setResponse(await recomputeScenarioValidity(scenarioCode));
    } catch (requestError) {
      setError((requestError as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    setError('');
    setConflict(null);
    try {
      await activateScenario(
        scenarioCode,
        reason.trim() ? { override_reason: reason.trim() } : {},
      );
      setActivated(true);
      onActivated?.();
      await load();
    } catch (requestError) {
      const body = activationConflict(requestError);
      if (body) setConflict(body as Record<string, unknown>);
      else setError((requestError as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const graded = Boolean(response?.verdict) && response?.current !== false;
  const blocking = Boolean(response?.blocking);
  const reasonLongEnough = reason.trim().length >= OVERRIDE_MIN_CHARS;
  const canActivate =
    !activeAlready &&
    !activated &&
    graded &&
    (!blocking || reasonLongEnough) &&
    !busy;

  const consequence = blocking
    ? `Activating ${scenarioCode} against the audit means trainees are graded by an answer key the audit says does not separate skill from clicking. Your reason is stored on the verdict you overrode.`
    : `Activating ${scenarioCode} starts grading trainees with this answer key and makes its definition read-only.`;

  const runtimeBlockers = conflict?.runtime_blockers as unknown[] | undefined;
  const gateConflict = conflict?.validity_gate as
    | Record<string, unknown>
    | undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={960}
      title={`Activate ${scenarioCode}${scenarioName ? ` — ${scenarioName}` : ''}`}
      destroyOnHidden
    >
      <div className="assessment-detail-stack">
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="Activation reads the stored verdict; it never computes it"
          description="The audit is measured separately so activation stays a fast locked decision. A verdict about a different answer key counts as no verdict at all."
        />

        {error && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title={error}
          />
        )}

        {runtimeBlockers && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title="The runtime preflight refused this activation"
            description={
              <ul>
                {runtimeBlockers.map((blocker, index) => (
                  <li key={index}>
                    {typeof blocker === 'string'
                      ? blocker
                      : JSON.stringify(blocker)}
                  </li>
                ))}
              </ul>
            }
          />
        )}

        {gateConflict && (
          <Alert
            className="assessment-callout"
            type="warning"
            showIcon
            title="The quality audit refused this activation"
            description={
              <div>
                <Typography.Paragraph>
                  {String(
                    gateConflict.message ||
                      'Explain the override below to activate anyway.',
                  )}
                </Typography.Paragraph>
                {gateConflict.action === 'recompute' && (
                  <Button onClick={recompute} loading={busy}>
                    Recompute the audit
                  </Button>
                )}
              </div>
            }
          />
        )}

        {conflict && !runtimeBlockers && !gateConflict && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title="This answer key cannot be activated as it stands"
            description={otherRefusal(conflict)}
          />
        )}

        {activated && (
          <Alert
            className="assessment-callout"
            type="success"
            showIcon
            title={`${scenarioCode} is active and its definition is read-only`}
          />
        )}

        <Card
          size="small"
          title="Evidence"
          extra={
            <Button onClick={recompute} loading={busy || loading}>
              Recompute the audit
            </Button>
          }
        >
          <ValidityEvidence response={response} />
        </Card>

        {!activeAlready && !activated && (
          <Card size="small" title="Activate">
            {!graded ? (
              <Alert
                className="assessment-callout"
                type="warning"
                showIcon
                title="The audit has not graded this answer key as it stands now"
                description="Recompute the audit first. Activation deliberately refuses to run on evidence about different content."
              />
            ) : (
              <>
                <Alert
                  className="assessment-callout"
                  type={blocking ? 'error' : 'warning'}
                  showIcon
                  title={consequence}
                />
                {blocking && (
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: '100%', marginBottom: 12 }}
                  >
                    <Typography.Text>
                      Why is it right to grade people with this answer key
                      anyway? At least {OVERRIDE_MIN_CHARS} characters; it is
                      stored on the verdict.
                    </Typography.Text>
                    <Input.TextArea
                      aria-label="Override reason"
                      rows={3}
                      value={reason}
                      onChange={event => setReason(event.target.value)}
                    />
                  </Space>
                )}
                <Popconfirm
                  title="Activate this answer key?"
                  description={consequence}
                  okText="Activate and lock"
                  cancelText="Not yet"
                  disabled={!canActivate}
                  onConfirm={activate}
                >
                  <Button
                    type="primary"
                    danger={blocking}
                    disabled={!canActivate}
                    loading={busy}
                  >
                    {blocking ? 'Override the audit and activate' : 'Activate'}
                  </Button>
                </Popconfirm>
              </>
            )}
          </Card>
        )}

        {activeAlready && (
          <Alert
            className="assessment-callout"
            type="info"
            showIcon
            title="This answer key is already active"
            description="An active definition is read-only. Changing it means a new version, minted and pushed as a draft, then activated in its place."
          />
        )}
      </div>
    </Drawer>
  );
};

export default ActivatePanel;
